import { reactive, computed, watch, onMounted, onUnmounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useTheme } from 'vuetify';
import { useAnalytics } from '@/utils/useAnalytics';
import { useDuckDBExport } from '@rdrive/data-duckdb';
import { useStatusTypeFilters } from '@/composables/useStatusTypeFilters';
import { useDashboard } from '@rdrive/plugin-dashboard-shell';

// The app's root component — an adapter over DashboardShell. Since 6b-2b the PLATFORM half of
// the boot (URL validation → settings, analytics init, the user id, token refresh, DuckDB init)
// and every platform service are the dashboard's (createDashboard / useDashboard() from
// @rdrive/plugin-dashboard-shell); this file keeps the app's half: theme, the welcome dialog's
// flags and Load button, the data load's trigger, the DuckDB export and the filter FAB.
export default () => {
  const store = useStore();
  const dashboard = useDashboard();
  const { t } = useI18n();
  const vuetifyTheme = useTheme();
  const currentTheme = computed(() => store.state.theme);

  // The boot context, parsed once from the iframe URL when the dashboard was created
  // (usePlatformParams). Read here for `theme` and the welcome dialog's `queryString`; the
  // dashboard's start() (onMounted) validates and applies the rest.
  const platformParams = dashboard.params;

  // The initial theme: the host's `theme` URL param first (HOST_APP_CONVENTIONS §3.1 — its OS
  // colour scheme; the host recreates the iframe when it changes), exactly `light`/`dark` or
  // ignored; else the OS scheme via matchMedia, as before. Dark is pushed to Vuetify at once
  // either way, so the first paint is already dark.
  const getUserColorScheme = () => {
    if (platformParams.theme) {
      if (platformParams.theme === 'dark') {
        vuetifyTheme.global.name.value = 'dark'; //set immediately
      }
      return platformParams.theme;
    }
    if (window.matchMedia) { 
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        vuetifyTheme.global.name.value = 'dark'; //set immediately 
        return 'dark';
      } else {
        return 'light';
      }
    
    } else {
      return currentTheme.value;
    }
  };

  const theme = ref(getUserColorScheme());
  store.commit('setTheme', theme.value);

  // The missing-params dialog and the DuckDB query dialog's open state live in DashboardShell;
  // `missingParams` is what the shell's warning dialog lists — the dashboard's, set by start().
  const state = reactive({
    open: [],
    headers: [
      { title: "Id", key: "clientId", sortable: true },
      { title: "Assigned", value: "itemTitle", sortable: true },
      { title: "Drawing", value: "itemDrawing", sortable: true },
      { title: "Location", value: "itemLocation", sortable: true },
      { title: "Updated Date", value: "itemUpdated", sortable: true },
      { title: "Status", value: "itemStatus", sortable: true }
    ],
    filters: {},
    missingParams: dashboard.missingParams,
  });

  const menuItems = computed(() => [
    { name: 'Project', icon: 'mdi-home-outline', text: t('project') },
    { name: 'Teams', icon: 'mdi-account-multiple-outline', text: t('teams') },
    { name: 'Drawings', icon: 'mdi-map', text: t('drawings') },
    { name: 'Documents', icon: 'mdi-file-document-outline', text: t('documents') },
    { name: 'Controllers', icon: 'mdi-account-check-outline', text: t('controllers') },
    { name: 'Activity', icon: 'mdi-poll', text: t('activity') }
  ]);

  const settings = computed(() => dashboard.settings);
  const loadDashboard = computed(() => store.getters.getLoadDashboard);

  const buildMode = ref('production');

  // The welcome dialog's progress comes from the dashboard's data loader: `done` counts every
  // boot table that settled (loaded or failed, as the old hand count did), `total` is the
  // manifest's boot (non-lazy) tables — src/data/manifest.js.
  const dataLoader = dashboard.loader;
  const apiProgress = computed(() => dataLoader.progress.done);
  const totalApiRequests = computed(() => dataLoader.progress.total);

  const { trackEvent } = useAnalytics();

  // --- DuckDB query dialog (C1) -------------------------------------------
  // `db` only exists once DuckDB has initialised: the dashboard's `db` ref is set then
  const { exporting, exportDatabase } = useDuckDBExport({
    conn: computed(() => dashboard.conn),
    db: dashboard.db
  });

  // --- Welcome dialog (C2) ------------------------------------------------
  const showWelcomeDialog = computed({
    get: () => store.getters.getShowWelcomeDialog,
    set: (value) => store.dispatch('updateShowWelcomeDialog', value)
  });
  const isDuckDBInitialized = computed(() => dashboard.isDuckDBInitialized.value);
  const projectName = computed(() => dashboard.settings.projectName);
  const dataLoaded = computed(() => dashboard.dataLoaded.value);
  const queryString = computed(() => platformParams.queryString);

  const handleLoadDashboard = async () => {
    trackEvent('loadDashboard', {
      action_type: 'button_click',
      project_code: dashboard.settings.projectCode,
      project_name: dashboard.settings.projectName
    });

    await store.dispatch('updateLoadDashboard', true);
  };

  const handleCopyQueryString = () => {
    trackEvent('copyQueryString', {
      action_type: 'avatar_double_click',
      project_code: dashboard.settings.projectCode,
      project_name: dashboard.settings.projectName
    });
  };

  watch(dataLoaded, (newValue) => {
    if (newValue) {
      store.dispatch('updateShowWelcomeDialog', false);
    }
  });

  // --- global status / item-type filters (D3) -----------------------------
  // The only place in the app that writes BOTH filter contexts. Writable
  // computeds so the composable stays store-free.
  const showFilterDialog = ref(false);

  const statusFilters = computed({
    get: () => store.state.statusFilters,
    set: (value) => store.commit('setStatusFilters', value)
  });

  const itemTypeFilters = computed({
    get: () => store.state.itemTypeFilters,
    set: (value) => store.commit('setItemTypeFilters', value)
  });

  const statusTypeFilters = useStatusTypeFilters({
    conn: computed(() => dashboard.conn),
    dataLoaded,
    isInitialized: isDuckDBInitialized,
    contexts: {
      items: computed(() => dashboard.filters.items),
      statusHistory: computed(() => dashboard.filters.statusHistory)
    },
    statusFilters,
    itemTypeFilters
  });

  // The FAB is packaged (@rdrive/plugin-status-filter) and cannot resolve `filterDialog.<key>` — item-type
  // vocabulary is per-dashboard. The app owns the labels: the composable keeps working in
  // KEYS ('issues' | 'processes'), and this maps them to the [{ key, label }] contract.
  // `statusTypeFilters.itemTypes` is the composable's plain module-level ITEM_TYPES array,
  // not a ref — no `.value`. `t` is reactive, so labels re-render on a locale switch.
  const itemTypeOptions = computed(() =>
    statusTypeFilters.itemTypes.map((key) => ({ key, label: t(`filterDialog.${key}`) }))
  );

  // DashboardShell has already closed its missing-params dialog when it emits this.
  const handleMissingParams = () => {
    window.close();
  };

  const getData = async () => {
    try {

      if (!dashboard.isDuckDBInitialized.value) {
        console.error('DuckDB not initialized yet. Please wait for initialization to complete.');
        return;
      }

      if (!dashboard.conn) {
        throw new Error('DuckDB connection not initialized');
      }

      // The whole load is the manifest's (src/data/manifest.js), run by the dashboard's data
      // loader: the 13 boot tables in parallel, the status_templates re-rank (ddl.post), then
      // the config's onLoaded hook — the lookups and the items filter context — and only THEN
      // dataLoaded = true, which is what closes this dialog and starts every page's queries.
      // A failed table is logged by the loader and the load carries on, as it always did.
      await dataLoader.load();

      store.commit('setShowWelcomeDialog', false);
      store.commit('setLoadDashboard', false);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const toggleTheme = () => {
    theme.value = theme.value === 'dark' ? 'light' : 'dark';
    vuetifyTheme.global.name.value = theme.value;
    store.commit('setTheme', theme.value);
  };

  const colorSchemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
  colorSchemeQuery.addEventListener('change', (e) => {  
    theme.value = e.matches ? 'dark' : 'light';
    vuetifyTheme.global.name.value = theme.value;
  });

  watch(
    () => [
      settings.value.scopedToken, 
      settings.value.projectCode,
      loadDashboard.value
    ],
    ([newScopedToken, newProjectCode, newLoadDashboard]) => {
      if (newScopedToken && newProjectCode && newLoadDashboard) {
        getData();
      }
    }
  );


  const updateTheme = (e) => {
    theme.value = e.matches ? 'dark' : 'light';
    store.commit('setTheme', theme.value);
  };

  const handleItemsUpdated = () => {
    // Handle items updated event
    //console.log('Items updated event received');
  };

  onMounted(async () => {
    buildMode.value = 'webapp';

    // The platform half of the boot is the dashboard's: it validates the URL first, then writes
    // the settings, configures analytics, logs the user id, arms the token refresh and
    // initialises DuckDB. A missing param stops it right after its console.error, while the
    // shell's dialog shows — no token refresh, no DuckDB, so no data load — and stops this hook
    // too: no updateBuildMode, no store theme listener.
    const started = await dashboard.start();
    if (!started) {
      return; // Stop further initialization if parameters are invalid
    }

    store.dispatch('updateBuildMode', buildMode.value);
    store.commit('setTheme', theme.value);
    colorSchemeQuery.addEventListener('change', updateTheme);
    // Fullscreen (the kebab's toggle and the fullscreenchange listeners) is DashboardShell's.
  });

  onUnmounted(() => {
    dashboard.stop(); // the token refresher
    colorSchemeQuery.removeEventListener('change', updateTheme);
  });

  return {
    theme,
    state,
    menuItems,
    apiProgress,
    totalApiRequests,
    exporting,
    exportDatabase,
    showWelcomeDialog,
    isDuckDBInitialized,
    projectName,
    dataLoaded,
    queryString,
    handleLoadDashboard,
    handleCopyQueryString,
    showFilterDialog,
    statusFilters,
    itemTypeFilters,
    statusTypeFilters,
    itemTypeOptions,
    handleMissingParams,
    toggleTheme,
    handleItemsUpdated
  };
};
